/* eslint-disable react-hooks/exhaustive-deps */
/**
 * /partner — Partner-with-us landing + contact form.
 *
 * Visual language matches the uploaded "Partner with Rescigo Health"
 * reference: coral-red primary (#D23247), clean white canvas,
 * eyebrow + split headline + supporting copy + two CTAs, hero image
 * with floating workflow cards, a 4-card partner categories grid, a
 * 6-cell platform benefits grid, a 5-step "How partnership works"
 * strip, and finally a contact form that posts to
 * /api/public/partner-enquiry.
 *
 * Mobile-first: hero stacks, categories collapse to 2 cols, benefits
 * stack to 2 cols, process stacks vertically on narrow screens.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowRight, Pill, TestTube, Ambulance, Buildings, Users,
  Calendar, Gear, Laptop, CreditCard, Headset, FileText,
  CheckCircle, Handshake, CaretRight,
} from "@phosphor-icons/react";
import { MarketingNav, MarketingFooter } from "./_shared";
import { usePageImages } from "../../lib/pageImages";
import SEO, {
  CITY_BRANCHES, organizationSchema, localBusinessSchema,
} from "../../lib/seo";

const CATEGORIES = [
  {
    key: "pharmacy", title: "Pharmacy\nPartners", Icon: Pill,
    body: "Receive medicine enquiries, manage prescription requests and coordinate customer deliveries through your partner dashboard.",
    photo: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=800&q=70",
  },
  {
    key: "diagnostic_lab", title: "Diagnostic\nLaboratory Partners", Icon: TestTube,
    body: "List eligible diagnostic services, receive test bookings, coordinate home sample collection and manage secure report delivery.",
    photo: "https://images.unsplash.com/photo-1579165466741-7f35e4755660?auto=format&fit=crop&w=800&q=70",
  },
  {
    key: "ambulance", title: "Ambulance\nPartners", Icon: Ambulance,
    body: "Receive transport enquiries, manage vehicle capabilities and availability, and coordinate patient transfers.",
    photo: "https://images.unsplash.com/photo-1587745416684-47953f16f02f?auto=format&fit=crop&w=800&q=70",
  },
  {
    key: "hospital_clinic", title: "Hospital & Clinic\nPartners", Icon: Buildings,
    body: "Connect hospital services with diagnostics, pharmacy, medical transport, home care and patient support workflows.",
    photo: "https://images.unsplash.com/photo-1587351021355-a479a299d2f9?auto=format&fit=crop&w=800&q=70",
  },
];

const BENEFITS = [
  { Icon: Users,     title: "Customer Discovery",
    body: "Help customers in your service area find and request your services." },
  { Icon: Calendar,  title: "Booking & Enquiry Management",
    body: "Review requests, confirm availability and manage service status." },
  { Icon: Gear,      title: "Operational Coordination",
    body: "Coordinate deliveries, appointments, sample collection and transport." },
  { Icon: Laptop,    title: "Partner Dashboard",
    body: "Access service requests, customer communications and operational records." },
  { Icon: CreditCard,title: "Payments & Settlements",
    body: "Review transactions, agreed service charges and settlement records." },
  { Icon: Headset,   title: "Partner Support",
    body: "Access onboarding guidance, account assistance and platform updates." },
];

const PROCESS = [
  { n: "01", title: "Register Your Business",
    body: "Select your partner category and submit your organisation's basic details." },
  { n: "02", title: "Complete Verification",
    body: "Provide relevant licences, registrations, professional credentials and service information." },
  { n: "03", title: "Configure Your Services",
    body: "Set your coverage area, operating hours, service catalogue and availability preferences." },
  { n: "04", title: "Review Commercial Terms",
    body: "Understand applicable fees, responsibilities, settlement arrangements and service policies." },
  { n: "05", title: "Get Activated",
    body: "Following approval and onboarding, access your partner dashboard and begin managing eligible enquiries." },
];

const CATEGORY_OPTIONS = [
  { value: "pharmacy",         label: "Pharmacy" },
  { value: "diagnostic_lab",   label: "Diagnostic lab" },
  { value: "ambulance",        label: "Ambulance operator" },
  { value: "hospital_clinic",  label: "Hospital / clinic" },
  { value: "hygiene",          label: "Hygiene / cleaning service" },
  { value: "biomedical_waste", label: "Biomedical waste collector" },
  { value: "other",            label: "Other" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function CategoryCard({ c }) {
  const [a, b] = c.title.split("\n");
  const Icon = c.Icon;
  return (
    <article data-testid={`partner-cat-${c.key}`}
             className="group rounded-3xl overflow-hidden bg-white border border-[#F0EEEA]
                         shadow-sm hover:shadow-md transition-shadow">
      <div className="p-5 flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#FDE2E4] text-[#D23247]
                         grid place-items-center shrink-0">
          <Icon size={22} weight="duotone"/>
        </div>
        <div className="min-w-0">
          <h3 className="font-heading text-lg text-slate-900 leading-tight">
            {a}<br/>{b}
          </h3>
        </div>
      </div>
      <div className="relative h-32 overflow-hidden bg-[#F7F6F2]">
        <img src={c.photo} alt="" loading="lazy"
              className="absolute inset-0 w-full h-full object-cover
                          group-hover:scale-[1.03] transition-transform"/>
      </div>
      <div className="p-5 pt-4">
        <p className="text-[13px] text-slate-600 leading-relaxed">{c.body}</p>
        <a href="#partner-contact"
            data-testid={`partner-cat-${c.key}-explore`}
            className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#D23247]">
          Explore Partnership <CaretRight size={12} weight="bold"/>
        </a>
      </div>
    </article>
  );
}

function BenefitCard({ b }) {
  const Icon = b.Icon;
  return (
    <div className="p-5 rounded-2xl bg-white border border-[#F0EEEA]
                     hover:border-[#D23247] transition-colors flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-[#FDE2E4] text-[#D23247]
                       grid place-items-center shrink-0">
        <Icon size={20} weight="duotone"/>
      </div>
      <div className="min-w-0">
        <div className="font-heading text-base text-slate-900">{b.title}</div>
        <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">{b.body}</p>
      </div>
    </div>
  );
}

function ContactForm() {
  const empty = {
    name: "", email: "", phone: "", company: "", category: "other",
    city: "", message: "", website: "",   // honeypot
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Fill in name, email, phone and message");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/partner-enquiry`, form,
                           { timeout: 15000 });
      setDone(true);
      setForm(empty);
      toast.success("Thanks! Our team will reach out shortly.");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      toast.error(typeof d === "string" ? d :
                   Array.isArray(d) ? d.map(x => x.msg).join("; ") :
                   "Could not submit, please try again");
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div data-testid="partner-form-done"
            className="rounded-2xl bg-[#DCFCE7] border border-[#86EFAC] p-8 text-center">
        <CheckCircle size={44} weight="duotone" className="text-[#15803D] mx-auto"/>
        <h3 className="font-heading text-xl mt-3 text-slate-900">Thanks for reaching out!</h3>
        <p className="text-sm text-slate-700 mt-2">
          Our partnerships team will contact you within one business day.
        </p>
        <button onClick={() => setDone(false)}
                 className="mt-5 text-sm font-medium text-[#D23247] hover:underline">
          Submit another enquiry
        </button>
      </div>
    );
  }

  const inputCls = "h-11 w-full px-3 border border-[#E5E2D9] rounded-md text-sm outline-none focus:border-[#D23247]";

  return (
    <form onSubmit={submit} data-testid="partner-form"
           className="rounded-2xl bg-white border border-[#F0EEEA] p-5 sm:p-7 shadow-sm space-y-4">
      {/* Honeypot — hidden from humans */}
      <input tabIndex={-1} autoComplete="off" value={form.website}
              onChange={update("website")} name="website"
              className="absolute left-[-9999px] opacity-0 pointer-events-none"
              aria-hidden="true"/>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
            Full name *
          </label>
          <input value={form.name} onChange={update("name")} required
                  placeholder="Your name" data-testid="partner-field-name"
                  className={inputCls}/>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
            Company / Clinic
          </label>
          <input value={form.company} onChange={update("company")}
                  placeholder="Organisation name" data-testid="partner-field-company"
                  className={inputCls}/>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
            Email *
          </label>
          <input type="email" value={form.email} onChange={update("email")}
                  required placeholder="name@company.com"
                  data-testid="partner-field-email"
                  className={inputCls}/>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
            Phone *
          </label>
          <input value={form.phone} onChange={update("phone")}
                  required placeholder="+91 …" data-testid="partner-field-phone"
                  className={inputCls}/>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
            Partner type
          </label>
          <select value={form.category} onChange={update("category")}
                   data-testid="partner-field-category"
                   className={inputCls + " bg-white"}>
            {CATEGORY_OPTIONS.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
            City / Service area
          </label>
          <input value={form.city} onChange={update("city")}
                  placeholder="Thiruvananthapuram" data-testid="partner-field-city"
                  className={inputCls}/>
        </div>
      </div>
      <div>
        <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">
          Tell us about your service *
        </label>
        <textarea value={form.message} onChange={update("message")} required
                   rows={4} data-testid="partner-field-message"
                   placeholder="What do you offer, how many customers do you serve, what help do you need from Rescigo?"
                   className="w-full p-3 border border-[#E5E2D9] rounded-md text-sm outline-none focus:border-[#D23247] resize-none"/>
      </div>
      <button type="submit" disabled={busy} data-testid="partner-form-submit"
               className="h-12 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 rounded-full
                           bg-[#D23247] hover:bg-[#B52236] text-white text-sm font-semibold
                           disabled:opacity-60 transition-colors">
        {busy ? "Sending…" : "Submit enquiry"} <ArrowRight size={14} weight="bold"/>
      </button>
      <p className="text-[11px] text-slate-500">
        By submitting you agree to be contacted by our partnerships team. We don't share your details with third parties.
      </p>
    </form>
  );
}

export default function PartnerLanding() {
  const images = usePageImages("partner");
  const jsonLd = [
    organizationSchema(),
    ...CITY_BRANCHES.map(localBusinessSchema),
  ];
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Partner with us", path: "/partner" },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="page-partner">
      <SEO
        title="Partner with Rescigo Health — pharmacies, labs, ambulances, hospitals"
        description="Grow your pharmacy, diagnostic lab, ambulance service, hospital or hygiene business with the Rescigo Health partner network across Kerala and India."
        path="/partner"
        keywords="rescigo partner, healthcare partner Kerala, pharmacy partner, diagnostic lab partner, ambulance partner, hospital partner, hygiene partner, biomedical waste partner"
        jsonLd={jsonLd}
        breadcrumbs={breadcrumbs}
      />
      <MarketingNav/>

      {/* ============================================================
          HERO
          ============================================================ */}
      <section className="pt-24 sm:pt-28 pb-10 sm:pb-14 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-start">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 font-medium">
              — Partner Network
            </div>
            <h1 className="font-heading text-[36px] sm:text-5xl lg:text-[56px] leading-[1.08] mt-5 text-slate-900">
              Partner with Rescigo Health.<br/>
              <span className="text-[#D23247]">
                Grow your reach.<br/>Simplify your operations.
              </span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mt-5 max-w-xl leading-relaxed">
              Connect your healthcare business with customers, service enquiries
              and digital coordination tools through the Rescigo Health partner
              network. Whether you operate a pharmacy, diagnostic laboratory,
              ambulance service or hospital, we're building a connected platform
              to make healthcare easier to discover, request and coordinate.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href="#partner-contact"
                  data-testid="partner-hero-become"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full
                              bg-[#D23247] hover:bg-[#B52236] text-white
                              text-sm font-semibold transition-colors">
                Become a Partner <ArrowRight size={14} weight="bold"/>
              </a>
              <a href="#partner-benefits"
                  data-testid="partner-hero-explore"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full
                              border border-[#D23247] text-[#D23247] hover:bg-[#FDE2E4]
                              text-sm font-semibold transition-colors">
                Explore Partner Solutions <ArrowRight size={14} weight="bold"/>
              </a>
            </div>
          </div>

          {/* Right: hero image + floating cards */}
          <div className="relative">
            <div className="relative rounded-[32px] overflow-hidden
                             aspect-[4/3] sm:aspect-[5/4] bg-[#FDE2E4]">
              <img
                src={images["hero.main"] || "https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?auto=format&fit=crop&w=1200&q=70"}
                alt="Hospital team collaborating with a pharmacy partner"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            {/* Floating workflow cards (hidden on mobile for breathing room) */}
            {[
              { Icon: Pill,      label: "New Medicine Enquiry", sub: "From Thiruvananthapuram", top: "top-4",     left: "left-4" },
              { Icon: TestTube,  label: "Lab Test Booking",     sub: "Home collection request", top: "top-24",    left: "left-4" },
              { Icon: Ambulance, label: "Transport Enquiry",    sub: "Patient transfer request",top: "top-44",    left: "left-4" },
              { Icon: Laptop,    label: "Partner Dashboard",    sub: "Manage your services",    top: "top-[256px]",left: "left-4" },
            ].map((c, i) => {
              const Ico = c.Icon;
              return (
                <div key={i}
                      className={`hidden md:flex absolute ${c.top} ${c.left}
                                   bg-white rounded-xl shadow-xl border border-[#F0EEEA]
                                   p-2.5 pr-4 items-center gap-2.5 max-w-[220px]`}>
                  <div className="w-9 h-9 rounded-lg bg-[#FDE2E4] text-[#D23247]
                                   grid place-items-center shrink-0">
                    <Ico size={18} weight="duotone"/>
                  </div>
                  <div>
                    <div className="text-[12px] font-semibold text-slate-900 leading-tight">{c.label}</div>
                    <div className="text-[10px] text-slate-500 leading-tight">{c.sub}</div>
                  </div>
                  <CaretRight size={12} className="text-slate-400"/>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          CATEGORIES
          ============================================================ */}
      <section className="pb-14 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 font-medium">
          — One network. Built around your business.
        </div>
        <div className="flex items-end justify-between mt-3 gap-4 flex-wrap">
          <h2 className="font-heading text-3xl sm:text-4xl text-slate-900">Partner categories</h2>
          <a href="#partner-contact" className="text-sm font-semibold text-[#D23247] inline-flex items-center gap-1">
            Explore all partner categories <ArrowRight size={12} weight="bold"/>
          </a>
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CATEGORIES.map(c => <CategoryCard key={c.key}
              c={{ ...c, photo: images[`categories.${c.key}`] || c.photo }}/>)}
        </div>
      </section>

      {/* ============================================================
          BENEFITS
          ============================================================ */}
      <section id="partner-benefits" className="pb-16 max-w-7xl mx-auto px-5 lg:px-10">
        <h2 className="font-heading text-3xl sm:text-4xl text-slate-900">
          More than a listing. <span className="text-[#D23247]">A connected service platform.</span>
        </h2>
        <p className="text-sm sm:text-base text-slate-600 mt-2 max-w-2xl">
          Tools, workflows and support to help your healthcare business grow.
        </p>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BENEFITS.map((b, i) => <BenefitCard key={i} b={b}/>)}
        </div>
      </section>

      {/* ============================================================
          PROCESS
          ============================================================ */}
      <section className="pb-16 bg-[#FDE2E4]/40">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 font-medium">
            — How partnership works
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl text-slate-900 mt-3">
            A simple and transparent process.
          </h2>
          <p className="text-sm text-slate-600 mt-2 max-w-xl">
            From registration to activation, we make it easy to get started.
          </p>
          <ol className="mt-7 grid grid-cols-1 md:grid-cols-5 gap-4">
            {PROCESS.map((p, i) => (
              <li key={p.n} className="rounded-2xl bg-white border border-[#F0EEEA] p-4 relative">
                <div className="text-[11px] font-mono text-[#D23247]">{p.n}</div>
                <div className="w-9 h-9 rounded-xl bg-[#FDE2E4] text-[#D23247]
                                 grid place-items-center mt-2">
                  <FileText size={18} weight="duotone"/>
                </div>
                <div className="font-heading text-base text-slate-900 mt-3">{p.title}</div>
                <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">{p.body}</p>
                {i < PROCESS.length - 1 && (
                  <ArrowRight size={14} weight="bold"
                               className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 text-[#D23247]"/>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ============================================================
          CONTACT FORM
          ============================================================ */}
      <section id="partner-contact" className="py-16 max-w-7xl mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div>
            <div className="w-14 h-14 rounded-2xl bg-[#FDE2E4] text-[#D23247]
                             grid place-items-center">
              <Handshake size={28} weight="duotone"/>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl text-slate-900 mt-4">
              Join a growing network<br/>
              <span className="text-[#D23247]">of trusted partners.</span>
            </h2>
            <p className="text-slate-600 mt-4 max-w-md">
              Share a few details — our partnerships team reviews every
              enquiry and responds within one business day with the right
              onboarding path for your business.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-slate-700">
              {[
                "Dedicated onboarding + verification support",
                "Digital tools for bookings, dispatch and settlements",
                "Transparent commercial terms, no hidden fees",
                "Marketing visibility to local customers",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle size={16} weight="fill" className="text-[#15803D] mt-0.5 shrink-0"/>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <ContactForm/>
        </div>
      </section>

      <MarketingFooter/>
    </div>
  );
}
